/**
 * @name 回森JSBridge
 * @author Li Wei<liwei03@kuaishou.com>
 * @date 2021.06.24 15:19:35
 * @document 
 * （1）JSBridge: https://docs.corp.kuaishou.com/k/home/VbvRU3RfnG14/fcAAlG-7_Btrqqg6fS7lfD89s
 * （2）URL参数约定: https://docs.corp.kuaishou.com/k/home/VFPmLBu-xkMI/fcAD1N7ZF6Aiq7R9DAmhIqqeJ
 */

import { getJSBridgeCallBack, isIOS, isAndroid, compareAppVersion, getAppVersion, getSystem } from '@lib/utils.js';
import yoda from '@ks/yoda-js-sdk';

const bridgeNameSpace = 'imv';

// appReady函数
export const invokeApp = (method, params = {}, callback, version, isShowUpdateToast = true) => {
    console.log('invoke:', bridgeNameSpace, method, JSON.stringify(params));

    // 版本校验
    if(version && !compareAppVersion(version)){
        if(isShowUpdateToast){
            openAppToast(`您的App版本过低，请升级到${version}以上版本使用此功能`);
        }
        console.log(`invoke cancel! ${method} need version: ${version}, current version: ${getAppVersion() || undefined }`);
        return;
    }

    const config = {
        ...params,
        callback: method === 'requestDataByNative' ? callback : getJSBridgeCallBack(callback, method),
    }

    if (window[bridgeNameSpace] && typeof window[bridgeNameSpace] === 'object') {
        if(method.indexOf('.') !== -1){
            const arr = method.split('.');
            window?.[bridgeNameSpace]?.[arr[0]]?.[arr[1]](JSON.stringify(config));
        }else{
            window?.[bridgeNameSpace]?.[method](JSON.stringify(config));
        }
    // 兼容yoda容器
    }else{
        yoda.ready(() => {
            if(method.indexOf('.') !== -1){
                const arr = method.split('.');
                yoda?.[bridgeNameSpace]?.[arr[0]]?.[arr[1]](config);
            }else{
                yoda?.[bridgeNameSpace]?.[method](config);
            }
        });
    }
};

// 设置titlebar展示文字
export const setAppTitleText = (text, callback) => {
    invokeApp('setTitlebarText', { text }, callback);
};

// 调用客户端toast
export const openAppToast = (text, callback) => {
    invokeApp('toast', { text }, callback);
};

/**
 * 打开新的webview
 * @param {String} url 链接地址
 */
 export const openAppWebView = (url, callback) => {
    invokeApp('openURLInNewWebView', { url }, callback);
};

// 关闭WebView
export const closeAppWebView = (callback) => {
    invokeApp('close', {}, callback);
};

// 关闭所有webview
export const closeAppAllWebView = (callback) => {
    invokeApp('closeAll', {}, callback);
};

// 调用客户端登录
export const openAppLogin = (callback) => {
    invokeApp('login', {}, callback);
};

/**
 * 通用 brage， ios 请求后台数据
 * @param method GET or POST
 * @param urlString 后台的 url 地址
 * @param data 网络请求参数，post请求和get请求都用这个
 * @param urlParameters 网络请求参数，专门用来在post请求时存放get参数
 * @param callback callback function
 */
 export function request(params = {}, callback) {
    console.log(params);
    const { method = 'GET', urlString = '', data = {}, urlParameters = {} } = params;
    const methodNative = 'requestDataByNative';

    // 判断业务是否支持webp
    // const isWebp = checkSupportWebp();
    // isWebp && params & params.data ? Object.assign(params.data, { imgFormat: 'webp' }) : '';

    const jsCallBack = getJSBridgeCallBack(
        res => {
            // 兼容android低端手机，其webview做多了一层url encode
            if (/^%7B%5C%22/.test(res)) {
                console.log('raw response is:', res);
                res = decodeURIComponent(res);
                res = res.replace(/\\"/g, '"');
            }

            // console.log('response is:', res);

            if (typeof res === 'string') {
                callback && callback(JSON.parse(res));
                console.log('response is:', JSON.parse(res));
            }
        },
        methodNative,
        urlString,
        params.data,
        urlParameters
    );

    invokeApp(methodNative, { 
        method,
        urlString,
        parameters: data,
        getParameters: urlParameters,
    }, jsCallBack);

    console.log(
        `request is: `,
        JSON.stringify({
            method,
            urlString,
            parameters: data,
            getParameters: urlParameters,
            callback: jsCallBack,
        })
    );
}

// 触觉反馈 IOS ['light', 'medium', 'heavy']
export const hapticsFeedbackIOS = (type = 'light', callback) => {
    invokeApp('hapticsFeedbackIOS', { type }, callback);
};

// 触觉反馈 Android
export const hapticsFeedbackAndroid = (duration = 100, callback) => {
    invokeApp('hapticsFeedbackAndroid', { duration }, callback);
};

// 触觉反馈 Harmony
export const hapticsFeedbackHarmony = (duration = 100, callback) => {
    invokeApp('hapticsFeedbackHarmony', { duration }, callback);
};

// 基础触觉反馈
export const basicHapticsFeedback = () => {
    switch(getSystem()){
        case 'android':
            hapticsFeedbackAndroid();
            break;
        case 'ios':
            hapticsFeedbackIOS();
            break;
        case 'harmony':
            hapticsFeedbackHarmony();
            break;
    }
};

// 打开群弹窗
export const openAppGroupPopup = (type = 'official', data = {}, callback) => {
    invokeApp('openGroupPopup', { type, data }, callback);
};

/**
 * 建立客户端通知监听
 * @param {String} name 通知名称
 * @param {String} sender 指定发送者
 */
const notificationCache = {};
export const observerNativeNotification = (name, callback) => {
    // 只有IOS 3.7.0+支持headsetConnect和headsetDisconnect
    if((name === 'headsetConnect' || name === 'headsetDisconnect') && !isIOS()){
        return;
    }
    if(notificationCache[name]?.length){
        notificationCache[name].push(callback);
    }else{
        notificationCache[name] = [callback];
    }
    invokeApp('addNotificationObserver', { name }, (res) => {
        console.log(`enter addNotificationObserver-${name}-callback: `, res)
        if(notificationCache[name].length){
            notificationCache[name].forEach((callback) => {
                callback(res);
            })
        }
    });
};

/**
 * 统计打点
 */
 export const sendAppLog = (params = {}, callback) => {
    invokeApp('sendLog', { ...params }, callback);
    // 2021.7.21 为了兼容IOS客户端的新处理方式 我们通过客户端生命周期来手动保证从别处返回本页面时能够正确上报pageName
    if(params.type.toLowerCase() === 'page' && isIOS()){
        observerNativeNotification('webView.viewDidAppear', () => {
            invokeApp('sendLog', { ...params, action: 'ENTER' }, callback);
        });

        observerNativeNotification('webView.viewDidDisappear', () => {
            invokeApp('sendLog', { ...params, action: 'LEAVE' }, callback);
        });
    }
};

// 获取本地存储数据
export const getAppStorage = (name, callback) => {
    invokeApp('getStorage', { name }, callback);
};

// 设置本地存储数据
export const setAppStorage = (name, value, callback) => {
    invokeApp('setStorage', { name, value }, callback);
};

// 删除本地存储数据
export const removeAppStorage = (name, callback) => {
    invokeApp('removeStorage', { name }, callback);
};

// 清空本地存储数据
export const clearAppStorage = (callback) => {
    invokeApp('clearStorage', {}, callback);
};

// 唤起选择列表
export const openAppSelectList = (title = '', list = [], cancel = '取消', achievement, callback) => {
    invokeApp('selectList', { title, list, cancel, achievement }, callback);
};

// 唤起成就弹窗
export const openAppAchievementModal = (item = {}, headUrl = '', type = 'edit', callback) => {
    invokeApp('achievementModal', { item, headUrl, type }, callback);
};

// 分享
export const openAppShare = (params, callback) => {
    invokeApp('share', params, callback);
};

// 通知客户端webview开始播放音频
export const notifyAppStartPlayAudio = (callback) => {
    invokeApp('startPlayAudio', {}, callback);
};

// 通知客户端webview结束播放音频
export const notifyAppEndPlayAudio = (callback) => {
    invokeApp('endPlayAudio', {}, callback);
};

// 参与话题弹窗
export const openAppJoinTopicModal = (name = '', id = '', callback) => {
    invokeApp('joinTopicModal', { name, id }, callback);
};

// 加入天团弹窗
export const openAppJoinGroupModal = (item = {}, callback) => {
    invokeApp('joinGroupModal', item, callback);
};

// 钻石充值
export const rechargeAppDiamond = (item = {}, callback) => {
    invokeApp('rechargeDiamond', item, callback);
};

// 是否展示钻石相关
export const isShowAppDiamond = (callback) => {
    invokeApp('isShowDiamond', {}, callback);
};

// 设置天团标签
export const setAppGroupTag = (tag = [], interestTag = [], callback) => {
    invokeApp('setGroupTag', { tag, interestTag }, callback);
};

// 设置App请求拦截 开启/关闭 主要用于解决IOS拦截开启状态下axios发送POST请求会丢失body的问题
export const setAppRequestIntercept = (enable, callback) => {
    if(isIOS()){
        invokeApp('setRequestInterceptEnable', { enable }, callback);
    }
};

// 打开快捷充值面板
export const openAppRechargePanel = (from, callback) => {
    invokeApp('openRechargePanel', { from }, callback);
};

// 刷新评论皮肤资源
export const refreshAppCommentSkin = (url, callback) => {
    invokeApp('refreshCommentSkin', { url }, callback);
};

// 是否处于青少年模式
export const isAppTeenagerMode = (callback) => {
    invokeApp('isTeenagerMode', {}, callback, '3.21.0');
};

// 进入实名认证流程
export const openAppRealNameVerification = (url, callback) => {
    invokeApp('openRealNameVerification', { url }, callback, '3.22.0');
};

// 进入提现流程
export const openAppWithdraw = (url, callback) => {
    invokeApp('openWithdraw', { url }, callback, '3.22.0');
};

// 打开首充礼包预览
export const openAppFirstChargeGift = (from, callback) => {
    invokeApp('openFirstChargeGift', { from }, callback, '3.23.0');
};

// 打开VIP充值面板
export const openAppVIPPanel = (isShowRightsBtn, callback) => {
    invokeApp('openVIPPanel', { isShowRightsBtn }, callback, '3.23.0');
};

// 打开活跃人物宝箱
export const openAppTaskBox = (item = {}, callback) => {
    invokeApp('openTaskBox', item, callback, '3.33.0');
};

// 下载图片到本地相册
export const downloadAppImage = (data = {}, callback) => {
    invokeApp('downloadImage', { data }, callback, '3.36.5');
};

// 刷新头像挂件资源
export const refreshAppHeadWidget = (url, callback) => {
    invokeApp('refreshHeadWidget', { url }, callback, '3.40.0', false);
};

// 获取临时登录cookie
export const getAppTempLoginCookie = (callback) => {
    invokeApp('getTempLoginCookie', {}, callback, '3.40.0');
};

// 刷新个人页关系信息
export const refreshAppProfileRelations = (uid, callback) => {
    invokeApp('refreshProfileRelations', { uid }, callback, '3.41.0', false);
};

// 关闭所有webview
export const closeAppOtherWebView = (callback) => {
    invokeApp('closeOther', {}, callback, '3.51.8', false);
};

// 打开新的半屏webview
 export const openAppHalfScreenWebView = (url, callback) => {
    invokeApp('openURLInHalfScreenWebView', { url }, callback, '3.56.0');
};

// 点击游戏控制按键
export const clickAppGameControl = (keycode, action, name, desc, callback) => {
    invokeApp('keyboardEvent', { keycode, action, name, desc }, callback, '3.58.0');
};

// 开启游戏
export const openAppGame = (item = {}, callback) => {
    invokeApp('openGame', { item }, callback, '3.58.0');
};

// 关闭游戏
export const closeAppGame = (item = {}, callback) => {
    invokeApp('closeGame', { item }, callback, '3.58.0');
};

// 弹窗
export const openAppDialog = ({
    title = '', 
    content = '',
    confirmBtnText = '知道了',
    cancelBtnText = '取消',
    type = 'alert',
}, callback) => {
    invokeApp('openAppDialog', { title, content, confirmBtnText, cancelBtnText, type }, callback, '3.61.0');
};

// 获取2023年度报告多维打分图片
export const getApp2023ScoreImageData = (params = {}, callback) => {
    invokeApp('radarGraph', { ...params }, callback);
};

// 获取客户端存储权限
export const getAppStoragePermission = (callback) => {
    invokeApp('retrieveStoragePermission', {}, callback, '3.62.0');
};

// 刷新哄哄话题列表
export const refreshAppHongTopicList = (callback) => {
    invokeApp('refreshHongTopicList', {}, callback, '3.66.5', false);
};

// 获取每日签到任务提醒状态
export const getAppSignTaskReminderStatus = (callback) => {
    invokeApp('getSignTaskReminderStatus', {}, callback, '3.69.0', false);
};

// 设置每日签到提醒状态
export const setAppSignTaskReminderStatus = (open, callback) => {
    invokeApp('setSignTaskReminderStatus', { open }, callback, '3.69.0');
};

// 获取客户端相机权限(仅IOS实现，Android以另外的方式实现了，无需前端关注)
export const getAppCameraPermission = (callback) => {
    if(isIOS()){
        invokeApp('cameraPermission', {}, callback, '3.87.0');
    }
};

// 给指定用户发送表情
export const sendAppSticker = (userId, sticker, callback) => {
    invokeApp('sendSticker', { userId, sticker: JSON.stringify(sticker) }, callback);
};

// 通知客户端更新个人页
export const needUpdateAppProfile = (data = {}, callback) => {
    invokeApp('needUpdateProfile', data, callback, '3.99.0', false);
};

// 通知指定用户发送文本私信
export const sendAppTextMessage = (userId, text, callback) => {
    invokeApp('sendTextMessage', { userId, text }, callback, '3.101.0' );
};

// 阻止客户端拦截滑动
export const setAppStopSwiper = (isStop, callback) => {
    if(isAndroid()){
        invokeApp('setOutsideDisallowInterceptTouch', { enable: isStop ? '1' : '0' }, callback, '3.122.0', false);
    }
};

/**
 * 为活动添加一个日历事件
 * @param {String} sceneCode 活动标识，用于日历事件确定
 * @param {String} title 活动名称
 * @param {String} desc 活动描述（可以带短链）
 * @param {String} start 首日开始UTC时间戳
 * @param {String} end 首日结束UTC时间戳，通常结束为开始+1分钟即可
 * @param {Number} days 首日开始循环天，安卓和ios需要区分，例如要一天，安卓传入1，ios则传入0
 */
export const addAppCalenderReminder = (params, callback) => {
    invokeApp('addCalenderReminder', { ...params }, callback, '3.141.0');
}
// 查询活动日历事件是否添加
export const checkAppCalenderReminder = (sceneCode, callback) => {
    invokeApp('checkCalenderReminder', { sceneCode }, callback);
}

// 删除日历
export const delAppCalenderReminder = (sceneCode, callback) => {
    invokeApp('delCalenderReminder', { sceneCode }, callback, '3.141.0');
}